import React, { useState } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template component
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductComparisonChart from './components/productComparisonChart';
import SuspectEmailCaptureFormSection from './components/suspectEmailCaptureFormSection';
import UnderstandingProduct from './components/understandingProduct';
// images
import highResDesktopImageWebp from './components/heroSection/images/whole-life-insurance-hero.webp';
import highResMobileImageWebp from './components/heroSection/images/whole-life-insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  HighestFinancialStrengthRatings,
  NerdWalletAllRightsReserved,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Life Insurance',
          linkUrl: '/life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Life Insurance',
        },
        {
          pageName: 'Whole Life Insurance',
          linkUrl: '/life-insurance/whole-life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Whole Life Insurance',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Unless you cancel your policy or stop paying your premiums.
        </>,
        <>
          <sup>2</sup>&nbsp;The primary purpose of whole life insurance is to provide a death benefit. Your policy&#39;s cash value typically becomes a useful source of funds only after several years of premium payments, which allows the cash value to build up. Each method of utilizing your policy&#39;s cash value has advantages and disadvantages and is subject to different tax consequences. Surrenders of, withdrawals from and loans against a policy will reduce the policy&#39;s cash surrender value and death benefit and may also affect any dividends paid on the policy. As a general rule, surrenders and withdrawals are taxable to the extent they exceed the cost basis of the policy, while loans are not taxable when taken. Loans taken against a life insurance policy can have adverse effects if not managed properly. Policy loans and automatic premium loans, including any accrued interest, must be repaid in cash or from policy values upon policy termination or the death of the insured. Repayment of loans from policy values (other than death proceeds) can potentially trigger a significant tax liability, and there may be little or no cash value remaining in the policy to pay the tax. If loans equal or exceed the cash value, the policy will terminate if additional cash payments are not made. Policyowners should consult with their tax advisors about the potential impact of any surrenders, withdrawals or loans.
        </>,
        <>
          <sup>3</sup>&nbsp;Dividends are reviewed annually by the company and are not guaranteed. Some policies may not receive dividends in a particular year or years even while other policies receive dividends. For universal life products, in lieu of dividends, experience is reflected through changes to nonguaranteed charges and credits.
        </>,
        <>
          {NerdWalletAllRightsReserved(4)}
        </>,
        <>
          <sup>5</sup>&nbsp;Assumes that no subsequent policy changes are made, and the policy is whole life coverage only and not blended with term life. While many term policies feature premiums that increase over time, premiums for Level Term Life Insurance will not increase.
        </>,
        <>
          <sup>6</sup>&nbsp;The Accelerated Care Benefit &#40;ACB&#41; is only available as an optional benefit rider with a Northwestern Mutual Life Insurance Company Whole Life Policy. The benefits provided by the ACB are intended to be excludable from federal gross income under Section 101&#40;g&#41; of the Internal Revenue Code as amended. The ACB is not intended to be a federally tax&#45;qualified long&#45;term care insurance contract under Section 7702B of the Internal Revenue Code &#40;IRC&#41;, as amended. Benefits provided by the ACB may be taxable if the Policy is not owned by the Insured.
        </>,
        <>
          {HighestFinancialStrengthRatings(7)}
        </>,
      ]}
      customDisclaimers={[
        <>
          To be used with forms ICC17.UU.WL. &#40;0119&#41; ICC17.UU.SWL.&#40;0119&#41; ICC17.UU.WL.BTB.&#40;0119&#41;, ICC17.UU.SWL.BTB.&#40;0119&#41;, ICC17.UU.WL.AP.&#40;0119&#41;, ICC17.UU.SWL.AP.&#40;0119&#41;, ICC18.UU.ACB.&#40;0119&#41;,ICC13.TT.WL.TQ.&#40;0114&#41;, ICC17.UU.WL.SOPE.&#40;0119&#41;, ICC18.UU.CUL.SOPE.&#40;0919&#41;, ICC17.UU.WL.WP.&#40;0119&#41;, ICC17.UU.APB.&#40;0119&#41;, NN 1 LIFE ADB, ICC18.UU.CUL.SMP.&#40;0919&#41;. Not all contracts available in all states.
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#45;2685&#45;91 &#40;0824&#41;</>}
      disableDisclaimerTopBorder>

      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResDesktopImageWebp} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResMobileImageWebp} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImageWebp}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      <ExperienceCallout />

      <FinancialPlanningSection />

      <ProductComparisonChart />

      <Faq />

      <SuspectEmailCaptureFormSection
        config={config}
        loadRecaptcha={loadRecaptcha}
        setLoadRecaptcha={setLoadRecaptcha}
      />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText={<>Let&#39;s boost your whole life insurance knowledge</>}
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
